import { Icon } from '@coop/components';
import { ChevronRightIcon } from '@coop/icons';
import { useEffect } from 'react';

import type { Bonus2026FlyInTriggerProps } from './Bonus2026FlyInTrigger.types';

import ECouponPriceSplash, {
    ECouponPriceSplashSkeleton,
} from '../../../common/components/ECouponPriceSplash';
import { useBonusLevelTimelineSummaryQuery } from '../../../myCoop/hooks/useBonusLevelTimelineSummaryQuery';
import styles from './Bonus2026FlyInTrigger.module.scss';
import {
    getBonus2026FlyInTriggerAriaLabel,
    getBonus2026FlyInTriggerData,
    getBonus2026FlyInTriggerLoadingAriaLabel,
} from './Bonus2026FlyInTrigger.utils';

const Bonus2026FlyInTrigger = ({
    onClick,
    onError: onTimelineError,
    ref,
    label,
    splashSize = 'default',
    ...rest
}: Bonus2026FlyInTriggerProps) => {
    const timelineSummaryQuery = useBonusLevelTimelineSummaryQuery();

    useEffect(() => {
        if (timelineSummaryQuery.isError) {
            onTimelineError?.();
        }
    }, [onTimelineError, timelineSummaryQuery.isError]);

    const isLoading = timelineSummaryQuery.isLoading;
    const {
        bonusValueText,
        progressPercent,
        bonusProgressMonetaryText: bonusProgressMonetary,
    } = getBonus2026FlyInTriggerData(timelineSummaryQuery.data);

    const ariaLabel = isLoading
        ? getBonus2026FlyInTriggerLoadingAriaLabel(label)
        : getBonus2026FlyInTriggerAriaLabel({
              label,
              bonusValueText,
              bonusProgressMonetaryText: bonusProgressMonetary,
          });

    return (
        <button
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            ref={ref}
            type="button"
            className={styles.Container}
            onClick={onClick}
            aria-label={ariaLabel}
            aria-busy={isLoading}
        >
            {isLoading ? (
                <div className={styles.Content}>
                    <span className={styles.LabelSkeleton} aria-hidden />
                    <div className={styles.Trailing}>
                        <ECouponPriceSplashSkeleton size={splashSize} />
                        <Icon icon={<ChevronRightIcon />} size="medium" aria-hidden />
                    </div>
                </div>
            ) : (
                <div className={styles.Content}>
                    <span className={styles.Label} aria-hidden>
                        {label}
                    </span>
                    <div className={styles.Trailing}>
                        <ECouponPriceSplash
                            theme="primary"
                            text={bonusValueText}
                            size={splashSize}
                            ariaHidden
                        />
                        <Icon icon={<ChevronRightIcon />} size="medium" aria-hidden />
                    </div>
                </div>
            )}
            <div className={styles.ProgressBarTrack} aria-hidden>
                <div className={styles.ProgressBarFill} style={{ width: `${progressPercent}%` }} />
            </div>
        </button>
    );
};

export default Bonus2026FlyInTrigger;
